import React, { useContext, useState, useEffect } from 'react';
import '../styles/NavBar.css';
import { Link, useNavigate } from 'react-router-dom';
import { PanierContext } from '../context/PanierContext';

function NavBar() {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "fr");
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [client, setClient] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const navigate = useNavigate();
  const { panier } = useContext(PanierContext);

  // 🛒 Nombre d'articles dans le panier (0 par défaut)
  const totalArticles = panier?.length ?? 0;

  // 🔔 Nombre de notifications non lues (0 par défaut)
  const unreadCount = notifications.filter(n => !n.read).length ?? 0;

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setShowLangMenu(false);
    window.dispatchEvent(new Event("langChanged"));
  };

  // Récupération client connecté
  useEffect(() => {
    const stored = localStorage.getItem('clientConnecte');
    setClient(stored ? JSON.parse(stored) : null);

    const onStorage = (e) => {
      if (e.key === 'clientConnecte') {
        setClient(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };

    const onClientUpdated = () => {
      const s = localStorage.getItem('clientConnecte');
      setClient(s ? JSON.parse(s) : null);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('clientUpdated', onClientUpdated);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('clientUpdated', onClientUpdated);
    };
  }, []);

  // Récupération notifications
  useEffect(() => {
    const fetchNotifications = () => {
      const stored = JSON.parse(localStorage.getItem("notifications")) || [];
      setNotifications(stored);
    };

    fetchNotifications();

    const handleUpdate = () => fetchNotifications();
    window.addEventListener("notificationsUpdated", handleUpdate);

    return () => window.removeEventListener("notificationsUpdated", handleUpdate);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('clientConnecte');
    window.dispatchEvent(new Event('clientUpdated'));
    setClient(null);
    navigate('/');
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-top">
            <Link to="/" className="logo logo-link">
              <img src="/images/logotantsaha.png" alt="Logo" className="logo-image" />
              <span className="logo-text">Tsenan'ny Tantsaha</span>
            </Link>

            {/* 🌍 Langue mobile */}
            <div className="lang-mobile">
              <button className="lang-btn" onClick={() => setShowLangMenu(!showLangMenu)}>
                <img src={lang === "fr" ? "/images/fr.png" : "/images/md.png"} alt="Langue actuelle" className="flag-icon" />
                <span className="lang-code">{lang.toUpperCase()}</span>
              </button>
              {showLangMenu && (
                <ul className="lang-dropdown">
                  <li onClick={() => handleLangChange("fr")}>
                    <img src="/images/fr.png" alt="Français" className="flag-icon" />
                    <span>FR</span>
                  </li>
                  <li onClick={() => handleLangChange("mg")}>
                    <img src="/images/md.png" alt="Malagasy" className="flag-icon" />
                    <span>MG</span>
                  </li>
                </ul>
              )}
            </div>

            {/* 🛒 Panier mobile */}
            <div className="panier-mobile">
              <Link to="/panier" className="panier-icon-only">
                <img src="/icons/monpanier.png" alt="Panier" className="icon-panier-img" />
                <span className="panier-badge">{totalArticles}</span> {/* Toujours afficher 0 */}
              </Link>
            </div>

            <ul className="nav-links-desktop">
              <li><Link to="/produits">{lang === "fr" ? "Produits" : "Vokatra"}</Link></li>
              <li><Link to="/categories">{lang === "fr" ? "Catégories" : "Sokajy"}</Link></li>
              <li><Link to="/contact">{lang === "fr" ? "Contact" : "Fifandraisana"}</Link></li>

              {/* 🛒 Panier desktop */}
              <li>
                <Link to="/panier" className="panier-icon">
                  <img src="/icons/monpanier.png" alt="Panier" className="icon-panier-img" />
                  <span className="panier-badge">{totalArticles}</span>
                </Link>
              </li>

              {/* 🔔 Notifications */}
              <li className="nav-notifications">
                <button className="notif-btn">
                  🔔
                  <span className="notif-badge">{unreadCount}</span>
                </button>

                <ul className="notif-dropdown">
                  {notifications.length === 0 ? (
                    <li>Aucune notification</li>
                  ) : (
                    notifications.slice().reverse().map((n) => (
                      <li
                        key={n.id}
                        className={n.read ? "read" : "unread"}
                        onClick={() => {
                          // Marquer comme lu correctement
                          const updated = notifications.map(noti =>
                            noti.id === n.id ? { ...noti, read: true } : noti
                          );
                          setNotifications(updated);
                          localStorage.setItem("notifications", JSON.stringify(updated));

                          navigate("/mes-commandes");
                        }}
                      >
                        {n.message}
                      </li>
                    ))
                  )}
                </ul>
              </li>

              {/* 👤 Client */}
              <li className="nav-client">
                {client ? (
                  <div className="client-wrapper">
                    <span className="client-nom">👤 {client.prenom} {client.nom}</span>
                    <ul className="client-menu">
                      <li><Link to="/profil">{lang === "fr" ? "Profil" : "Mombamomba"}</Link></li>
                      <li><Link to="/mes-commandes">{lang === "fr" ? "Mes Commandes" : "Ny baiko"}</Link></li>
                      <li><button className="logout-btn" onClick={handleLogout}>{lang === "fr" ? "Déconnexion" : "Hiala"}</button></li>
                    </ul>
                  </div>
                ) : (
                  <Link to="/information?simple=1">{lang === "fr" ? "Se connecter" : "Hiditra"}</Link>
                )}
              </li>

              {/* 🌍 Sélecteur de langue desktop */}
              <li className="lang-selector">
                <button className="lang-btn" onClick={() => setShowLangMenu(!showLangMenu)}>
                  <img src={lang === "fr" ? "/images/fr.png" : "/images/md.png"} alt="Langue actuelle" className="flag-icon" />
                  <span className="lang-code">{lang.toUpperCase()}</span>
                </button>
                {showLangMenu && (
                  <ul className="lang-dropdown">
                    <li onClick={() => handleLangChange("fr")}><img src="/images/fr.png" alt="FR" className="flag-icon" />FR</li>
                    <li onClick={() => handleLangChange("mg")}><img src="/images/md.png" alt="MG" className="flag-icon" />MG</li>
                  </ul>
                )}
              </li>
            </ul>

            <div className="burger" onClick={toggleMenu}>
              <div className="line" />
              <div className="line" />
              <div className="line" />
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        <ul className={`nav-links-mobile ${menuOpen ? 'active' : ''}`}>
          <li><Link to="/produits" onClick={() => setMenuOpen(false)}>{lang === "fr" ? "Produits" : "Vokatra"}</Link></li>
          <li><Link to="/categories" onClick={() => setMenuOpen(false)}>{lang === "fr" ? "Catégories" : "Sokajy"}</Link></li>
          <li><Link to="/contact" onClick={() => setMenuOpen(false)}>{lang === "fr" ? "Contact" : "Fifandraisana"}</Link></li>
          <li>
            {client ? (
              <>
                <span className="client-nom">{client.prenom} {client.nom}</span>
                <button className="logout-btn" onClick={handleLogout}>{lang === "fr" ? "Déconnexion" : "Hiala"}</button>
              </>
            ) : (
              <Link to="/information?simple=1" onClick={() => setMenuOpen(false)}>{lang === "fr" ? "Se connecter" : "Hiditra"}</Link>
            )}
          </li>
          {/* 🌍 Sélecteur langue mobile */}
          <li className="lang-selector-mobile">
            <button className="lang-btn" onClick={() => setShowLangMenu(!showLangMenu)}>
              <img src={lang === "fr" ? "/images/fr.png" : "/images/md.png"} alt="Langue actuelle" className="flag-icon" />
              <span className="lang-code">{lang.toUpperCase()}</span>
            </button>
          </li>
        </ul>
      </nav>
    </>
  );
}

export default NavBar;
